/**
 * product-card-hover-sizes.js
 *
 * Desktop enhancement: reveal the existing inline size overlay
 * (.quick-size-overlay) when a shopper HOVERS a product card, instead of
 * requiring a click on the quick-add button. The overlay, its variant
 * resolution, and its add-to-cart flow all already exist in the theme
 * (quick-cart-drawer.js) — this only changes the trigger from click to hover,
 * and only on desktop / fine-pointer devices. Touch/mobile is untouched: the
 * existing tap-the-button behavior remains, and this script no-ops there.
 *
 * Gated on the `card-qa-desktop--hover` body class (set from the
 * product_card_desktop_quick_add_mode theme setting). Desktop and mobile modes
 * are independent settings, so changing one never affects the other.
 */
(function () {
  'use strict';

  // Only on devices that actually hover with a precise pointer (desktop).
  // Phones/tablets fall through to the existing click behavior.
  var hoverCapable = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  if (!hoverCapable) return;

  // Respect the theme setting (desktop mode must be 'hover').
  if (!document.body.classList.contains('card-qa-desktop--hover')) return;

  // Belt-and-suspenders: never run below the theme's mobile breakpoint even on
  // a hover-capable narrow window (matches the 750px logic in quick-cart-drawer.js).
  function isDesktopWidth() {
    return window.innerWidth >= 750;
  }

  var OPEN_DELAY = 0;    // open immediately on hover — any delay makes the panel
                         // feel like it lags behind the card's own hover response
  var openTimer = null;
  var pendingCard = null;

  function qcd() {
    return document.querySelector('quick-cart-drawer');
  }

  function eligibleCard(el) {
    if (!el) return null;
    var card = el.closest('product-card');
    if (!card) return null;
    // Don't hijack cards rendered inside the cart drawer or the quick-cart modal.
    if (card.closest('.quick-cart-drawer') || card.closest('cart-drawer')) return null;
    // Only cards that actually have the inline overlay (multi-variant, available).
    if (!card.querySelector('.quick-size-overlay')) return null;
    return card;
  }

  function openFor(card) {
    var drawer = qcd();
    if (!drawer || typeof drawer.openInlineOverlay !== 'function') return;
    var overlay = card.querySelector('.quick-size-overlay');
    if (!overlay || overlay.classList.contains('is-active')) return;
    drawer.openInlineOverlay(overlay, card);
    // Tag the card itself so CSS can hide the quick-add (+) button reliably.
    // (A :has() selector proved unreliable here; an explicit class is deterministic.)
    card.classList.add('is-quick-sizes-open');
  }

  function closeActive() {
    var drawer = qcd();
    if (drawer && typeof drawer.closeInlineOverlay === 'function') {
      drawer.closeInlineOverlay();
    }
    clearOpenFlags();
  }

  function clearOpenFlags() {
    document.querySelectorAll('product-card.is-quick-sizes-open').forEach(function (c) {
      c.classList.remove('is-quick-sizes-open');
    });
  }

  function cancelPending() {
    if (openTimer) {
      clearTimeout(openTimer);
      openTimer = null;
    }
    pendingCard = null;
  }

  // Delegated hover in/out. Using mouseover/mouseout (which bubble) with a
  // relatedTarget check gives us reliable enter/leave semantics that also work
  // for cards added later by infinite scroll or filtering — no re-binding needed.
  document.addEventListener('mouseover', function (e) {
    if (!isDesktopWidth()) return;
    var card = eligibleCard(e.target);
    if (!card) return;

    // Already hovering the same card (moving between its children) — nothing to do.
    if (pendingCard === card || card.querySelector('.quick-size-overlay.is-active')) return;

    cancelPending();
    pruneStaleFlags();
    pendingCard = card;
    openTimer = setTimeout(function () {
      openTimer = null;
      // Confirm the pointer is still on this card before opening.
      if (pendingCard === card && card.matches(':hover')) {
        openFor(card);
      }
      pendingCard = null;
    }, OPEN_DELAY);
  });

  document.addEventListener('mouseout', function (e) {
    if (!isDesktopWidth()) return;
    var card = eligibleCard(e.target);
    if (!card) return;

    // Still inside the same card (moved onto a child, e.g. a size button)? keep open.
    var related = e.relatedTarget;
    if (related && card.contains(related)) return;

    // Left the card entirely.
    if (pendingCard === card) cancelPending();

    var overlay = card.querySelector('.quick-size-overlay.is-active');
    if (overlay) closeActive();
  });

  // The theme's own size-button handler closes the overlay directly (bypassing
  // closeActive), so clear the open flag on any size click to avoid stranding
  // the hidden state on the quick-add button.
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.quick-size-overlay__btn')) {
      setTimeout(clearOpenFlags, 0);
    }
  });

  // Safety net: drop flags from any card whose overlay is no longer active.
  function pruneStaleFlags() {
    document.querySelectorAll('product-card.is-quick-sizes-open').forEach(function (c) {
      var ov = c.querySelector('.quick-size-overlay');
      if (!ov || !ov.classList.contains('is-active')) c.classList.remove('is-quick-sizes-open');
    });
  }

  // If the viewport shrinks below desktop, drop any open hover overlay so we
  // don't strand the desktop panel on a now-mobile layout.
  window.addEventListener('resize', function () {
    if (!isDesktopWidth()) {
      cancelPending();
      clearOpenFlags();
      var active = document.querySelector('product-card .quick-size-overlay.is-active');
      if (active && !active.closest('.quick-cart-drawer') && !active.closest('cart-drawer')) {
        closeActive();
      }
    }
  });
})();
